import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  Eraser,
  Eye,
  EyeOff,
  KeyRound,
  LogIn,
  LogOut,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Sparkles,
  Trash2,
  Undo2,
  Upload,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * Normaliza un label de botón para buscarlo en el mapa:
 * saca acentos, baja a minúsculas, colapsa espacios y recorta.
 *
 * "  Guardar   CAMBIOS " -> "guardar cambios"
 * "Reenviar invitación"  -> "reenviar invitacion"
 */
export function normalizeButtonLabel(label: string): string {
  return label.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

// Mapa label -> icono. Las claves van ya normalizadas (minúsculas, sin
// acentos); hay un test que lo verifica para que un acento colado no deje una
// entrada muerta.
//
// La mayoría de las claves son un verbo suelto y cubren los labels compuestos
// por prefijo ("guardar" cubre "Guardar cambios", "Guardar fuentes", ...). Las
// claves de varias palabras existen solo para ganarle al prefijo más corto
// cuando el icono correcto es otro ("cerrar sesion" no es "Cerrar").
export const BUTTON_ICONS: Record<string, LucideIcon> = {
  // Guardar
  guardar: Save,
  'guardar cambios': Save,

  // Cancelar / cerrar / descartar
  cancelar: X,
  cerrar: X,
  'cerrar sesion': LogOut,
  rechazar: X,
  descartar: X,

  // Navegación
  volver: ArrowLeft,
  anterior: ChevronLeft,
  siguiente: ChevronRight,
  continuar: ArrowRight,
  ver: ArrowRight,
  'ver todo': ArrowRight,
  'mostrar mas': ChevronDown,
  'mostrar todo': ChevronDown,

  // Alta
  nuevo: Plus,
  nueva: Plus,
  crear: Plus,
  agregar: Plus,
  anadir: Plus,

  // Edición y baja
  editar: Pencil,
  eliminar: Trash2,
  borrar: Trash2,
  quitar: Trash2,
  limpiar: Eraser,
  deshacer: Undo2,

  // Transferencia de archivos
  exportar: Download,
  descargar: Download,
  plantilla: Download,
  importar: Upload,
  subir: Upload,

  // Reintento y refresco
  rotar: RefreshCw,
  regenerar: RefreshCw,
  reintentar: RefreshCw,
  actualizar: RefreshCw,
  recargar: RefreshCw,
  reenviar: RefreshCw,
  reiniciar: RotateCcw,

  // Confirmación
  aplicar: Check,
  confirmar: Check,
  aceptar: Check,

  // Varios
  buscar: Search,
  copiar: Copy,
  copiado: Check,
  generar: Sparkles,
  probar: Play,
  ejecutar: Play,
  lanzar: Play,
  enviar: Send,
  mostrar: Eye,
  ocultar: EyeOff,
  'iniciar sesion': LogIn,
  'cambiar contrasena': KeyRound,
};

// Claves ordenadas de más larga a más corta: así el prefijo más específico
// gana siempre, sin depender del orden en que se declaró el mapa.
const PREFIX_KEYS = Object.keys(BUTTON_ICONS).sort((a, b) => b.length - a.length);

/**
 * Deduce el icono de un botón a partir de su texto.
 *
 * Orden de resolución, de más específico a más general:
 *   1. Match exacto del label normalizado contra el mapa.
 *   2. Match por prefijo: la clave más larga tal que el label arranque con
 *      "<clave> ". El espacio es obligatorio, así "Verificar" no matchea "ver".
 *   3. Nada: el botón se renderiza sin icono. Nunca adivinamos.
 *
 * No hay paso de "contiene la palabra": un label como "Volver a canonical"
 * matchearía varias claves a la vez y el resultado dejaría de ser predecible.
 */
export function resolveButtonIcon(label: string | null): LucideIcon | null {
  if (label === null) return null;

  const normalized = normalizeButtonLabel(label);
  if (normalized === '') return null;

  const exact = BUTTON_ICONS[normalized];
  if (exact) return exact;

  for (const key of PREFIX_KEYS) {
    if (normalized.startsWith(`${key} `)) return BUTTON_ICONS[key] ?? null;
  }

  return null;
}
